'use strict';

import {
  getUniqueTemporaryID,
  objectAssignDeep,
  newDateFromDatestring,
  addToArrayIfNotExists,
  toggleInArray,
  removeFromArrayIfExists,
  metersPerSecondAsSecondsPerMile,
  metersPerSecondAsSecondsPerKilometer,
  secondsPerKilometerAsMetersPerSecond,
  secondsPerMileAsMetersPerSecond,
  yardsToMiles,
} from '../common/helpers.js'
import { v1 as uuidv1 } from 'uuid'

const DISCIPLINE_REST_DAY = 14;
export const DISCIPLINE_OTHER = 15;
const DISCIPLINE_RACE = 16;
export const DISCIPLINE_DIARY_ENTRY = 17;

/**
 * Training session object
 *
 * initial_values: an object containing initial values to populate the Training
 *   Session with.
 */
export const TrainingSession = function TrainingSession(initial_values) {

  var self = this;

  this.CREATOR_COACH = '1';
  this.CREATOR_ATHLETE = '2';
  this.CREATOR_GROUP = '3';

  if (typeof(initial_values) == 'undefined') {
    initial_values = {};
  }

  // Set up default values.
  this.id = null;
  this.setUniqueTemporaryID();
  this.uuid = uuidv1(),
  this.date = null;
  this.type = 'training_session';
  this.prescribed_training_notes = '';
  this.athlete_feedback = '';
  this.creator = { type: 2 }
  this.intensity = null;
  this.attached_training_logs = [];
  this.attachment_ids = [];
  this.completed_minutes = null;
  this.completed_swim_minutes = null;
  this.completed_bike_minutes = null;
  this.completed_run_minutes = null;
  this.tags_automatic = [];
  this.tags_hidden = [];
  this.tags_user = [];
  this.comments = { new: '' };
  this.training_log_completed_seconds = 0;
  this.training_log_completed_km = 0;
  this.training_log_completed_swim_seconds = 0;
  this.training_log_completed_swim_km = 0;
  this.training_log_completed_bike_seconds = 0;
  this.training_log_completed_bike_km = 0;
  this.training_log_completed_run_seconds = 0;
  this.training_log_completed_run_km = 0;
  this.activity_plan_uuids_to_add = [];
  this.activity_plan_uuids_to_remove = [];

  this.set_values = function (values) {
    Object.keys(values).forEach(key => {
      let value = values[key];
      if (value && typeof(value) == 'object' && value != null && (value.constructor != Array)) {
        self[key] = objectAssignDeep({}, value);
      }
      else {
        self[key] = value;
      }
    });
  }

  this.set_values(initial_values);
}

TrainingSession.prototype.setUniqueTemporaryID = function (refresh_id) {
  if (refresh_id || !this.temporary_id) {
    this.temporary_id = getUniqueTemporaryID('training_session');
  }
  if (refresh_id || !this.id) {
    this.id = 'temp-' + this.temporary_id;
  }
}

/**
 * Set a timestamp on this session before saving.
 *
 * By doing this, we can avoid the user interface updating when more than one
 * save operation has occurred.
 */
TrainingSession.prototype.setSaveTimestamp = function () {
  this.last_save_timestamp = Date.now();
}

/**
 * Set a timestamp on this session when something has been edited.
 *
 * By doing this, we know if the session has been updated since the last save time.
 */
TrainingSession.prototype.setUpdatedTimestamp = function () {
  this.last_updated_timestamp = Date.now();
}

TrainingSession.prototype.getISOWeekKey = function () {
  if (this.date) {
    let date = newDateFromDatestring(this.date);
    return date.getWeek() + '-' + date.getWeekYear();
  }
}

TrainingSession.prototype.overridden_group_parent_session = function () {
  if (
    this.creator.type == this.CREATOR_GROUP &&
    'group_parent_session' in this &&
    'use_this_for_plan' in this.group_parent_session &&
    !this.group_parent_session.use_this_for_plan
  ) {
    return true;
  }
  else {
    return false;
  }
}

TrainingSession.prototype.using_group_parent_session = function () {
  if (
    this.creator.type == this.CREATOR_GROUP &&
    'group_parent_session' in this &&
    'use_this_for_plan' in this.group_parent_session &&
    this.group_parent_session.use_this_for_plan
  ) {
    return true;
  }
  else {
    return false;
  }
}

TrainingSession.prototype.has_permanent_id = function () {
  return (!this.id || isNaN(this.id)) ? false : true;
}

TrainingSession.prototype.is_training_session = function () {
  if (this.discipline_id && this.discipline_id != DISCIPLINE_REST_DAY && this.discipline_id != DISCIPLINE_DIARY_ENTRY) {
    return true;
  }
  else {
    return false;
  }
}

TrainingSession.prototype.getDisciplineCategory = function () {
  switch(parseInt(this.discipline_id)) {
    case 1:
    case 2:
    case 3:
    case 18:
      return 'swim';
    case 4:
    case 5:
    case 6:
    case 19:
      return 'bike';
    case 7:
    case 8:
    case 9:
    case 20:
      return 'run';
    case 10:
    case 11:
    case 21:
      return 'brick';
    case 12:
    case 13:
    case 22:
      return 'gym';
    case 14:
      return 'rest';
    case 15:
      return 'other';
    case 16:
      return 'race';
    case 17:
      return 'diary';
    default:
      return 'none';
  }
}

TrainingSession.prototype.getDisciplineName = function () {
  switch(parseInt(this.discipline_id)) {
    case 1:
      return "Swim Tech";
    case 2:
      return "Swim Intervals";
    case 3:
      return "Swim Endurance";
    case 4:
      return "Bike Intervals";
    case 5:
      return "Turbo Intervals";
    case 6:
      return "Bike Endurance";
    case 7:
      return "Run Intervals";
    case 8:
      return "Run Endurance";
    case 9:
      return "Run Tempo";
    case 10:
      return "Brick Intervals";
    case 11:
      return "Brick Endurance";
    case 12:
      return "Gym Core/Specifics";
    case 13:
      return "Gym Lifting";
    case 14:
      return "Rest day";
    case 15:
      return "Other";
    case 18:
      return "Swim";
    case 19:
      return "Bike";
    case 20:
      return "Run";
    case 21:
      return "Brick";
    case 22:
      return "Gym";

  }
}

TrainingSession.prototype.getDistanceUnit = function () {
  if ('prescribed_km' in this) {
    return 'kilometers';
  }
  else if ('prescribed_miles' in this) {
    return 'miles';
  }
  else if (typeof(athlete_data) != 'undefined') {
    return athlete_data.distance_unit_preference;
  }
  else {
    return 'kilometers';
  }
}

TrainingSession.prototype.getSwimDistance = function (unit) {
  const distance_unit = this.getPoolUnit();
  const discipline_category = this.getDisciplineCategory();
  if (distance_unit == 'meters') {
    if (discipline_category == 'swim') {
      if (unit == 'km') {
        return this.prescribed_meters / 1000;
      }
    }
    else if (discipline_category == 'brick') {
      return this.prescribed_swim_meters / 1000;
    }
  }
  else if (distance_unit == 'yards') {
    if (unit == 'miles') {
      if (discipline_category == 'swim') {
        return yardsToMiles(this.prescribed_yards);
      }
      else if (discipline_category == 'brick') {
        return yardsToMiles(this.prescribed_swim_yards);
      }
    }
  }
}

TrainingSession.prototype.getBikeDistance = function (unit) {
  const distance_unit = this.getDistanceUnit();
  const discipline_category = this.getDisciplineCategory();
  if (distance_unit == 'kilometers') {
    if (discipline_category == 'bike') {
      if (unit == 'km') {
        return this.prescribed_km;
      }
    }
    else if (discipline_category == 'brick') {
      if (unit == 'km') {
        return this.prescribed_bike_km;
      }
    }
  }
  else if (distance_unit == 'miles') {
    if (unit == 'miles') {
      if (discipline_category == 'bike') {
          return this.prescribed_miles;
      }
      else if (discipline_category == 'brick') {
          return this.prescribed_bike_miles;
      }
    }
  }
}

TrainingSession.prototype.getRunDistance = function (unit) {
  const distance_unit = this.getDistanceUnit();
  const discipline_category = this.getDisciplineCategory();
  if (distance_unit == 'kilometers') {
    if (discipline_category == 'run') {
      if (unit == 'km') {
        return this.prescribed_km;
      }
    }
    else if (discipline_category == 'brick') {
      if (unit == 'km') {
        return this.prescribed_run_km;
      }
    }
  }
  else if (distance_unit == 'miles') {
    if (unit == 'miles') {
      if (discipline_category == 'run') {
        return this.prescribed_miles;
      }
      else if (discipline_category == 'brick') {
        return this.prescribed_run_miles;
      }
    }
  }
}

TrainingSession.prototype.getTotalDistance = function (unit) {
  const distance_unit = this.getDistanceUnit();
  const discipline_category = this.getDisciplineCategory();
  if (distance_unit == 'kilometers') {
    if (discipline_category != 'brick') {
      if (unit == 'km') {
        return this.prescribed_km;
      }
    }
    else {
      if (unit == 'km') {
        return this.prescribed_swim_km + this.prescribed_bike_km + this.prescribed_run_km;
      }
    }
  }
  else if (distance_unit == 'miles') {
    if (discipline_category != 'brick') {
      if (unit == 'miles') {
        return this.prescribed_miles;
      }
    }
    else {
      if (unit == 'miles') {
        return yardsToMiles(this.prescribed_swim_yards) + this.prescribed_bike_miles + this.prescribed_run_miles;
      }
    }

  }
}

TrainingSession.prototype.getPoolUnit = function () {
  if ('prescribed_yards' in this || 'prescribed_swim_yards' in this) {
    return 'yards';
  }
  else if ('prescribed_meters' in this || 'prescribed_swim_meters' in this) {
    return 'meters';
  }
  else if (typeof(athlete_data) != 'undefined') {
    return athlete_data.pool_unit_preference;
  }
  else {
    return 'meters';
  }
}

TrainingSession.prototype.collect_save_post_data = function () {

  var self = this;
  var discipline_category = this.getDisciplineCategory();

  var post_data = {}

  var property_names = [
    'id',
    'athlete_id',
    'group_id',
    'training_plan_id',
    'attachment_ids',
    'date',
    'time',
    'time_zone',
    'training_plan_day',
    'pool_unit',
    'diary_summary',
    'discipline_id',
    'intensity',
    'other_discipline',
    'prescribed_minutes',
    'prescribed_bike_minutes',
    'prescribed_run_minutes',
    'prescribed_swim_minutes',
    'prescribed_training_notes',
    'subtitle',
    'key_session',
    'coach_comments',
    'temporary_id',
    'uuid',
    'order',
    'remove_from_bin'
  ];

  for (var index in property_names) {
    var property_name = property_names[index];
    post_data[property_name] = self[property_name];
  }

  // These fields are handled a bit differently from a straight copy.
  if (discipline_category == 'swim') {
    if (this.getPoolUnit() == 'meters') {
      post_data.prescribed_distance = this.prescribed_meters;
    }
    else if (this.getPoolUnit() == 'yards') {
      post_data.prescribed_distance = this.prescribed_yards;
    }
  }
  else if (discipline_category == 'brick') {
    if (this.getPoolUnit() == 'meters') {
      post_data.prescribed_swim_distance = this.prescribed_swim_meters;
    }
    else if (this.getPoolUnit() == 'yards') {
      post_data.prescribed_swim_distance = this.prescribed_swim_yards;
    }
    if (this.getDistanceUnit() == 'kilometers') {
      post_data.prescribed_bike_distance = this.prescribed_bike_km;
      post_data.prescribed_run_distance = this.prescribed_run_km;
    }
    else if (this.getDistanceUnit() == 'miles') {
      post_data.prescribed_bike_distance = this.prescribed_bike_miles;
      post_data.prescribed_run_distance = this.prescribed_run_miles;
    }
  }
  else {
    if (this.getDistanceUnit() == 'kilometers') {
      post_data.prescribed_distance = this.prescribed_km;
    }
    else if (this.getDistanceUnit() == 'miles') {
      post_data.prescribed_distance = this.prescribed_miles;
    }
  }

  if (typeof(this.activity_plan_uuids_to_add) != 'undefined') {
    post_data.activity_plan_uuids_to_add = this.activity_plan_uuids_to_add.slice();
  }

  post_data.distance_unit = this.getDistanceUnit();
  post_data.pool_unit = this.getPoolUnit();

  return post_data;
}

TrainingSession.prototype.getCoachCalendarPath = function () {
  return '/coach/' + this.athlete_id + '/?date=' + this.date + '#training-session-' + this.id;
}

TrainingSession.prototype.duplicate = function () {
  return new TrainingSession(this);
}

TrainingSession.prototype.createNewActivityPlan = function (discipline) {

  let discipline_id = WORKOUT_DISCIPLINES.CYCLING;
  if (discipline == 'run') {
    discipline_id = WORKOUT_DISCIPLINES.RUNNING;
  }
  else if (discipline == 'swim') {
    discipline_id = WORKOUT_DISCIPLINES.SWIMMING;
  }

  let activity_plan = {
    'id': 'temp-' + getUniqueTemporaryID('activity-plan'),
    'uuid': uuidv1(),
    'discipline': discipline_id,
    'workout_steps': [],
    'training_session_primary_id': this.id,
    'training_session_ids': [this.id],
    'training_session_uuids': [this.uuid],
  };

  addToArrayIfNotExists(this.activity_plan_uuids_to_add, activity_plan.uuid)

  return new ActivityPlan(activity_plan);
}

TrainingSession.prototype.copy_plan = function () {

  let uuid = uuidv1();

  let discipline_category = this.getDisciplineCategory();

  let copy = new TrainingSession({
    discipline_id: this.discipline_id,
    discipline_display: this.discipline_display,
    intensity: this.intensity,
    prescribed_training_notes: this.prescribed_training_notes,
    subtitle: this.subtitle,
    key_session: this.key_session,
    other_discipline: discipline_category == 'diary' ? this.diary_summary : this.other_discipline,
    diary_summary: this.diary_summary,
    prescribed_minutes: this.prescribed_minutes,
    prescribed_swim_minutes: this.prescribed_swim_minutes,
    prescribed_bike_minutes: this.prescribed_bike_minutes,
    prescribed_run_minutes: this.prescribed_run_minutes,
    uuid: uuid,
    attachment_ids: this.attachment_ids,
  });

  if ('prescribed_yards' in this) {
    copy.prescribed_yards = this.prescribed_yards;
  }
  else if ('prescribed_meters' in this) {
    copy.prescribed_meters = this.prescribed_meters;
  }

  if ('prescribed_km' in this) {
    copy.prescribed_km = this.prescribed_km;
    copy.prescribed_bike_km = this.prescribed_bike_km;
    copy.prescribed_run_km = this.prescribed_run_km;
  }
  else {
    copy.prescribed_miles = this.prescribed_miles;
    copy.prescribed_bike_miles = this.prescribed_bike_miles;
    copy.prescribed_run_miles = this.prescribed_run_miles;
  }

  if (this.prescribed_swim_km) {
    copy.prescribed_swim_km = this.prescribed_swim_km; // This is needed too for taking a second copy before it saves.
    copy.prescribed_swim_meters = this.prescribed_swim_km * 1000;
  }

  if (this.activity_plan_uuids) {
    copy.activity_plan_uuids = this.activity_plan_uuids.slice();
    copy.activity_plan_uuids_to_add = this.activity_plan_uuids.slice();
  }

  return copy;
}

TrainingSession.prototype.hasDeletePermission = function (xhale_meta_data, athlete_data, group_data) {

  if (!this.has_permanent_id()) {
    // Session has not been saved at all yet, so obviously cannot be deleted yet.
    return false;
  }
  else if (this.attached_training_logs.length > 0 && (!this.athlete_id || !xhale_meta_data.user_id || this.athlete_id !== xhale_meta_data.user_id)) {
    // Cannot delete training sessions with uploaded training data unless the
    // session belongs to them.
    return false;
  }
  else if (this.creator.creator_id == xhale_meta_data.user_id) {
    // Can delete training sessions created by themselves.
    return true;
  }
  else if (this.template === true || !(this.template === false)) {
    // Templates can only be deleted by those who created them.
    // Template must be explicitly set to false to be considered
    // something other than a template.
    return false;
  }
  else if (this.creator.creator_company_id && xhale_meta_data.coaching_company_id && this.creator.creator_company_id === xhale_meta_data.coaching_company_id) {
    // Can delete training sessions created by others in a coach's company.
    return true;
  }
  else if (this.group_id && typeof(group_data) != 'undefined' && group_data && typeof(group_data.current_user_is_prescriber) != 'undefined' && group_data.current_user_is_prescriber === true) {
    // We are viewing this as a group, so allow editing of the group sessions
    // by anyone else with prescriber access.
    return true;
  }
  else if (this.athlete_id == xhale_meta_data.user_id && can_edit_training_plans_on_date(this.date)) {
    // Can delete their own training sessions if they are created by others
    // and they are allowed to edit their own training.
    return true;
  }
  else if (this.athlete_id != xhale_meta_data.user_id && typeof(athlete_data) != 'undefined' && athlete_data && typeof(athlete_data.coaching_start_datetime) == 'object') {
    // Can reschedule training if the current date is after coaching started.
    var training_session_date = newDateFromDatestring(this.date);
    var coaching_start_date = new Date(athlete_data.coaching_start_datetime.getTime() + (athlete_data.coaching_start_datetime.getTimezoneOffset() * 60000));
    coaching_start_date.setHours(0, 0, 0, 0, 0);
    if (coaching_start_date <= training_session_date) {
      return true;
    }
    else {
      return false;
    }
  }
  else {
    return false;
  }

}

export function can_edit_training_plans_on_date(date) {

  if (xhale_meta_data.can_create_sessions) {
    return true;
  }

  if (!date) {
    return false;
  }

  var date = newDateFromDatestring(date);
  var now = new Date();
  if (date < now) {
    return true;
  }
  else {
    return false;
  }
}

export const STEP_CONSTANTS = {
  'DURATION_TYPE_TIME': 0,
  'DURATION_TYPE_DISTANCE': 1,
  'DURATION_TYPE_REPEAT_QUANTITY': 6,

  'TARGET_TYPE_SPEED': 0,
  'TARGET_TYPE_SPEED_BY_ZONE': 50, // This is different to the FIT file standard. Refer to Python code for details.
  'TARGET_TYPE_HEART_RATE': 1,
  'TARGET_TYPE_HEART_RATE_BY_ZONE': 51, // This is different to the FIT file standard. Refer to Python code for details.
  'TARGET_TYPE_OPEN': 2,
  'TARGET_TYPE_POWER': 4,
  'TARGET_TYPE_POWER_PERCENTAGE_FTP': 5, // This is different to the FIT file standard. Refer to Python code for details.
  'TARGET_TYPE_POWER_BY_ZONE': 6, // This is different to the FIT file standard. Refer to Python code for details.
  'TARGET_TYPE_SWIM_STROKE': 11,
  'TARGET_TYPE_NOTE': 14,
}

export const SWIM_STROKES = {
    0: 'freestyle',
    1: 'backstroke',
    2: 'breaststroke',
    3: 'butterfly',
    4: 'drill',
    5: 'mixed',
    6: 'im',  // IM is a mixed interval containing the same number of lengths for each of: Butterfly, Backstroke, Breaststroke, Freestyle, swam in that order.
}

export const SWIM_EQUIPMENT_INACTIVE = -1;
export const SWIM_EQUIPMENT_NO_EQUIPMENT = 0;
export const SWIM_EQUIPMENT = {
  0: 'no equipment',
  1: 'fins',
  2: 'kickboard',
  3: 'paddles',
  4: 'pull buoy',
  5: 'snorkel',
}

export const INTENSITY = {
  'ACTIVE': 0,
  'REST': 1,
  'WARMUP': 2,
  'COOLDOWN': 3,
  'RECOVERY': 4,
  'INTERVAL': 5,
  'OTHER': 6,
}

export const NORMAL_TARGET_TYPES = [
  STEP_CONSTANTS.TARGET_TYPE_POWER,
  STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP,
  STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE,
]

export const WORKOUT_DISCIPLINES = {
  'RUNNING': 1,
  'CYCLING': 2,
  'SWIMMING': 5,
}

export const ActivityPlan = function ActivityPlan(initial_values) {

  // Set up default values.
  this.deleted_workout_steps = [];

  this.set_values = function (values) {
    Object.keys(values).forEach(key => {
      let value = values[key];
      if (key == 'workout_steps') {
        this[key] = [];
        for (let i = 0; i < value.length; i++) {
          this[key][i] = new WorkoutStep(value[i]);
        }
      }
      else if (value && typeof(value) == 'object' && value != null && (value.constructor != Array)) {
        this[key] = objectAssignDeep({}, value);
      }
      else {
        this[key] = value;
      }
    });
  }

  this.set_values(initial_values);
}

ActivityPlan.prototype.hasTimeStep = function () {
  let step_indexes = this.workout_steps ? Object.keys(this.workout_steps) : [];
  for (let i = 0; i < step_indexes.length; i++) {
    let step_index = step_indexes[i];
    if (this.workout_steps[step_index].duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) {
      return true;
    }
  }
  return false;
}

ActivityPlan.prototype.hasFTPPercentageStep = function () {
  let step_indexes = this.workout_steps ? Object.keys(this.workout_steps) : [];
  for (let i = 0; i < step_indexes.length; i++) {
    let step_index = step_indexes[i];
    if (this.workout_steps[step_index].target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
      return true;
    }
  }
  return false;
}

ActivityPlan.prototype.hasRequiredDataForExport = function (training_session, training_zones, discipline) {
  let result = {
    can_export: true,
    description: 'The workout is ready to be exported.'
  };

  // The training session must have been saved.
  if (!training_session.has_permanent_id()) {
    result.can_export = false;
    result.description = "You must first save this training session before you can export it.";
    return result;
  }

  let steps = this.getStepsAsSortedList(true);

  // Must have at least one step.
  if (steps.length == 0) {
    result.can_export = false;
    result.description = "The plan must have at least one step.";
    return result;
  }

  for (let i = 0; i < steps.length; i++) {
    // If a step is calculated by zone, the zone data must exist in the athlete's profile.
    if (
      steps[i].target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE ||
      steps[i].target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE ||
      steps[i].target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE
    ) {
      if (!steps[i].getZoneData(training_zones, discipline)) {
        result.can_export = false;
        result.description = "Zone data required for step " + (i + 1) + " is missing.";
        return result;
      }
    }

    // Don't allow exporting if the workout hasn't been saved.
    if (steps[i].section_has_changes) {
      result.can_export = false;
      result.description = "Changes in step " + (i + 1) + " have not been saved.";
      return result;
    }
  }

  return result;
}

ActivityPlan.prototype.checkExportStatusAsMRC = function (training_session, bike_ftp, training_zones, discipline) {
  const is_bike = (discipline == 'bike' || discipline == WORKOUT_DISCIPLINES.CYCLING);
  if (!is_bike) {
    return {
      can_export: false,
      description: 'MRC exports only support bike workouts.'
    };
  }
  if (!bike_ftp) {
    return {
      can_export: false,
      description: 'MRC workouts require you to set your FTP in your Xhale settings.'
    };
  }
  if (!this.hasTimeStep()) {
    return {
      can_export: false,
      description: 'MRC exports require each workout step to be based on duration.'
    };
  }
  return this.hasRequiredDataForExport(training_session, training_zones, discipline);
};

ActivityPlan.prototype.checkExportStatusAsZWO = function (training_session, bike_ftp, training_zones, discipline) {
  const is_bike = (discipline == 'bike' || discipline == WORKOUT_DISCIPLINES.CYCLING);
  if (!is_bike) {
    return {
      can_export: false,
      description: 'ZWO exports currently only support bike workouts.'
    };
  }
  if (!bike_ftp) {
    return {
      can_export: false,
      description: 'To export as ZWO you must first enter your FTP in your Xhale settings.'
    };
  }
  if (!this.hasTimeStep()) {
    return {
      can_export: false,
      description: 'ZWO export requires setting steps based on duration.'
    };
  }
  return this.hasRequiredDataForExport(training_session, training_zones, discipline);
};


ActivityPlan.prototype.checkExportStatusAsFIT = function (training_session, bike_ftp, training_zones, discipline) {
  return this.hasRequiredDataForExport(training_session, training_zones, discipline);
}

ActivityPlan.prototype.checkExportStatusAsERG = function (training_session, bike_ftp, training_zones, discipline) {
  const is_bike = (discipline == 'bike' || discipline == WORKOUT_DISCIPLINES.CYCLING);
  if (!is_bike) {
    return {
      can_export: false,
      description: 'ERG exports only support bike workouts.'
    };
  }
  if (!this.hasTimeStep()) {
    return {
      can_export: false,
      description: 'ERG workouts require setting each step duration by time.'
    };
  }
  if (this.hasFTPPercentageStep() && !bike_ftp) {
    return {
      can_export: false,
      description: 'This workout has steps based on FTP, but you have not set your bike FTP in your Xhale settings.'
    };
  }
  return this.hasRequiredDataForExport(training_session, training_zones, discipline);
};


ActivityPlan.prototype.getStepByIndex = function (step_index) {
  return this.workout_steps[step_index];
}

ActivityPlan.prototype.getStepsAsSortedList = function (exclude_repeats) {
  var steps = [];

  // Record the first step that relates to each repeat.
  let repeat_first_steps = {};
  Object.keys(this.workout_steps).forEach((index) => {
    let step = this.workout_steps[index];
    if (step.isRepeatStep() && !step.deleted) {
      repeat_first_steps[step.duration_value] = step.index;
    }
  });

  // Create an initial sorted list of steps.
  Object.keys(this.workout_steps).forEach((index) => {
    let step = this.workout_steps[index];
    if (!step.deleted) {
      steps.push(step);
    }
  });
  steps.sort(function (a, b) { return a.index - b.index });

  // Cycle through the steps in order and add repeats to the subsequent steps.
  let steps_in_repeat = 0;
  let current_repeat_step_index = null;
  for (let i = 0; i < steps.length; i++) {

    const step = steps[i];

    // This is a repeat step declaration rather than a normal step. We already
    // gathered the data we needed from that in the previous loop, so move on
    // with the next iteration.
    if (step.isRepeatStep()) {
      continue;
    }

    // Repeat sections end when they encounter the repeat step declaration.
    // So if the current repeat step declaration is before the current step, it
    // is time to break out of the repeat.
    if (current_repeat_step_index && current_repeat_step_index < step.index) {
      this.workout_steps[current_repeat_step_index].total_steps_in_repeat = steps_in_repeat;
      current_repeat_step_index = null;
      steps_in_repeat = 0;
    }

    // If this step is listed as the first in a repeat, record that we have
    // entered a new repeat block.
    if (step.index in repeat_first_steps) {
      current_repeat_step_index = repeat_first_steps[step.index];
    }

    // If this step is still in a repeat section, record some data about the
    // repeat. Otherwise, wipe out any existing repeat step data which may
    // exist if the step has moved.
    if (current_repeat_step_index) {
      this.workout_steps[step.index].repeat_step_index = current_repeat_step_index;
      this.workout_steps[step.index].position_in_repeat = steps_in_repeat;
      steps_in_repeat += 1;
    }
    else {
      this.workout_steps[step.index].repeat_step_index = null;
      this.workout_steps[step.index].position_in_repeat = null;
    }

  }

  // Finish up adding a count of steps in the repeat.
  if (current_repeat_step_index) {
    this.workout_steps[current_repeat_step_index].total_steps_in_repeat = steps_in_repeat;
  }

  var steps = []
  Object.keys(this.workout_steps).forEach((index) => {
    let step = this.workout_steps[index];
    if ((!exclude_repeats || !step.isRepeatStep()) && !step.deleted) {
      steps.push(step);
    }
  });

  steps.sort(function (a, b) { return a.index - b.index });

  return steps;
}

export const organiseSortedStepsAsNestedSteps = function (sorted_steps) {

  // Gather a list of repeat sections.
  let repeats = [];
  let repeat_key_to_index = {};
  for (let i = 0; i < sorted_steps.length; i++) {
    if (sorted_steps[i].isRepeatStep()) {
      repeats.push({
        repeat_step_data: sorted_steps[i],
        steps: [],
      });
      repeat_key_to_index[sorted_steps[i].index] = repeats.length - 1;
    }
    else if (!sorted_steps[i].repeat_step_index) {
      // For steps that are not part of a normal repeat section add them
      // to a list of a fake repeat section.
      if (repeats.length == 0 || repeats[repeats.length - 1].repeat_step_data != null) {
        repeats.push({
          repeat_step_data: null,
          steps: [],
        });
      }
      repeats[repeats.length - 1].steps.push(sorted_steps[i]);
    }
  }

  // Add the steps to repeat sections.
  for (let i = 0; i < sorted_steps.length; i++) {
    if (sorted_steps[i].repeat_step_index) {
      repeats[repeat_key_to_index[sorted_steps[i].repeat_step_index]].steps.push(sorted_steps[i]);
    }
  }

  let sections = [];
  for (let i = 0; i < repeats.length; i++) {
    sections.push(new WorkoutSection(repeats[i].repeat_step_data, repeats[i].steps));
  }

  return sections;
}

ActivityPlan.prototype.getStepsBySection = function () {
  let sorted_steps = this.getStepsAsSortedList();
  return organiseSortedStepsAsNestedSteps(sorted_steps);
}

/**
 * Get duration type from the given steps. This will either be all steps in the
 * activity in order to get the default duration type or a specific section.
 * If no valid steps, provides a default based on the activity as a whole.
 */
ActivityPlan.prototype.getDurationTypeFromSteps = function (steps) {

  // Select a duration type based on the existing step's duration types.
  // Default to time.
  let step_indexes = steps ? Object.keys(steps) : [];

  for (let i = 0; i < step_indexes.length; i++) {

    let step_index = step_indexes[i];

    // Ignore rest steps.
    if (steps[step_index].intensity == INTENSITY.REST) {
      continue;
    }

    if (
      steps[step_index].duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME ||
      steps[step_index].duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE
    ) {
      return steps[step_index].duration_type;
    }

  }

  return this.getDefaultDurationType();
}

ActivityPlan.prototype.getDefaultDurationType = function () {
  if (this.discipline == WORKOUT_DISCIPLINES.RUNNING) {
    return STEP_CONSTANTS.DURATION_TYPE_TIME;
  }
  else if (this.discipline == WORKOUT_DISCIPLINES.CYCLING) {
    return STEP_CONSTANTS.DURATION_TYPE_TIME;
  }
  else if (this.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
    return STEP_CONSTANTS.DURATION_TYPE_DISTANCE;
  }
}

/**
 * Update provided steps duration type. This will either be all steps in the
 * activity or a specific section. Returns a list of the altered steps.
 */
ActivityPlan.prototype.updateDurationType = function (new_duration_type, steps) {
  let altered_steps = [];
  Object.keys(steps).forEach((step_index) => {

    let step = steps[step_index];

    if (step.duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) {
      step.duration_type = new_duration_type;
      step.duration_value = (step.duration_value / 60) * 100000;
      altered_steps.push(step);
    }
    else if (step.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
      step.duration_type = new_duration_type;
      step.duration_value = (step.duration_value / 100000) * 60;
      altered_steps.push(step);
    }

  });

  return altered_steps;
}

/**
 * Add steps to the activity plan.
 */
ActivityPlan.prototype.updateStructuredTrainingSteps = function (steps, clear_previous_steps) {
  // Create a dictionary for the steps if there isn't one, or clear the
  // previous steps if we want to refresh everything.
  if (!this.workout_steps || clear_previous_steps == true) {
    this.workout_steps = [];
  }

  // Update the specific step that we changed.
  for (let i = 0; i < steps.length; i++) {
    this.workout_steps[steps[i].index] = steps[i];
  }

  // Mark that the workout steps were updated so we know to save changes.
  this.workout_steps_updated = true;
}

ActivityPlan.prototype.flattenNestedSteps = function (nested_steps) {
  let steps = [];

  for (let i = 0; i < nested_steps.length; i++) {
    for (let j = 0; j < nested_steps[i].steps.length; j++) {
      steps.push(nested_steps[i].steps[j]);
    }
    steps.push(nested_steps[i].repeat_step_data);
  }

  return steps;
}

ActivityPlan.prototype.refreshStepIndexes = function (steps) {

  // Re-set the step index so there are no gaps.
  let new_steps_array = [];
  for (let i = 0; i < steps.length; i++) {
    steps[i].index = i;
    new_steps_array.push(steps[i]);
  }

  // Update the repeat step starting indexes which may now have changed.
  let first_step_in_repeat_index = null;
  for (let i = 0; i < new_steps_array.length; i++) {

    let step = new_steps_array[i];

    if (step.duration_type == STEP_CONSTANTS.DURATION_TYPE_REPEAT_QUANTITY) {

      // Set repeat first step index.
      new_steps_array[i].duration_value = first_step_in_repeat_index;

      // Reset first step index so that we get a new one for the next repeat.
      first_step_in_repeat_index = null;

    }
    else if (first_step_in_repeat_index == null) {
      first_step_in_repeat_index = new_steps_array[i].index;
    }

  }

  return new_steps_array;
}

ActivityPlan.prototype.downloadURL = function (training_session_id, extension) {

  if (isNaN(training_session_id)) {
    throw 'No training session id provided';
  }

  return "/training-logs/download-structured-workout/" + training_session_id + '/' + this.id + '.' + extension;
}

ActivityPlan.prototype.fitDownloadURL = function (training_session_id) {
  return this.downloadURL(training_session_id, 'fit');
}

ActivityPlan.prototype.zwoDownloadURL = function (training_session_id) {
  return this.downloadURL(training_session_id, 'zwo');
}

ActivityPlan.prototype.ergDownloadURL = function (training_session_id) {
  return this.downloadURL(training_session_id, 'erg');
}

ActivityPlan.prototype.mrcDownloadURL = function (training_session_id) {
  return this.downloadURL(training_session_id, 'mrc');
}

/**
 * @param
 *
 * training_session: The session we want to apply the duplicate to.
 */
ActivityPlan.prototype.duplicate = function (training_session) {

  // Duplicate the activity plan, setting new IDs and only assigning this
  // training session to the plan.
  let duplicate_activity_plan = new ActivityPlan({});

  duplicate_activity_plan.id = 'temp-' + getUniqueTemporaryID('activity-plan');
  duplicate_activity_plan.uuid = uuidv1();
  duplicate_activity_plan.discipline = this.discipline;
  duplicate_activity_plan.training_session_primary_id = training_session.id;
  duplicate_activity_plan.training_session_ids = [training_session.id];
  duplicate_activity_plan.workout_steps_updated = true;

  duplicate_activity_plan.workout_steps = [];
  for (let i = 0; i < this.workout_steps.length; i++) {
    duplicate_activity_plan.workout_steps[i] = new WorkoutStep(Object.assign({}, this.workout_steps[i]));
    duplicate_activity_plan.workout_steps[i].id = null;
  }

  // On the old plan, remove the reference to the current training session.
  removeFromArrayIfExists(this.training_session_ids, training_session.id);

  // Ideally the training session would update automatically,
  // but since we currently have the state in two places we
  // just update it manually here as well.
  removeFromArrayIfExists(training_session.activity_plan_uuids, this.uuid);
  addToArrayIfNotExists(training_session.activity_plan_uuids, duplicate_activity_plan.uuid);
  addToArrayIfNotExists(training_session.activity_plan_uuids_to_remove, this.uuid);
  addToArrayIfNotExists(training_session.activity_plan_uuids_to_add, duplicate_activity_plan.uuid);

  return duplicate_activity_plan;
}


ActivityPlan.prototype.getDisciplineString = function () {
  if (!isNaN(this.discipline)) {
    if (this.discipline == WORKOUT_DISCIPLINES.RUNNING) {
      return 'run';
    }
    else if (this.discipline == WORKOUT_DISCIPLINES.CYCLING) {
      return 'bike';
    }
    else if (this.discipline == WORKOUT_DISCIPLINES.SWIMMING) {
      return 'swim';
    }
  }
}

ActivityPlan.prototype.getSaveData = function () {

  let activity_plan = {};
  activity_plan.id = this.id;
  activity_plan.uuid = this.uuid;
  activity_plan.discipline = this.discipline;
  activity_plan.deleted_workout_steps = this.deleted_workout_steps;
  activity_plan.training_session_primary_id = this.training_session_primary_id;
  activity_plan.training_session_ids = this.training_session_ids;

  if (this.workout_steps_updated) {
    activity_plan.workout_steps_updated = this.workout_steps_updated;
  }

  activity_plan.workout_steps = [];

  for (let i = 0; i < this.workout_steps.length; i++) {
    activity_plan.workout_steps.push(this.workout_steps[i].getSaveData());
  }

  return activity_plan;
}

/**
 * WorkoutSection object.
 */
export const WorkoutSection = function WorkoutSection(repeat_step_data, steps) {
  this.repeat_step_data = repeat_step_data;
  this.steps = steps;
}

WorkoutSection.prototype.getTotalDistance = function (unit) {
  let cumulative_km = 0;
  for (let i = 0; i < this.steps.length; i++) {
    cumulative_km += this.steps[i].getDurationInKM() * this.steps[i].individual_step_repetitions;
  }

  if (unit == 'kilometers') {
    return cumulative_km;
  }
  else if (unit == 'meters') {
    return cumulative_km * 1000;
  }
  else {
    throw 'Unrecognised unit requested';
  }

}

WorkoutSection.prototype.getTotalTime = function () {
  let cumulative_seconds = 0;
  for (let i = 0; i < this.steps.length; i++) {
    const duration = this.steps[i].getDurationInSeconds() * this.steps[i].individual_step_repetitions;
    if (!duration) {
      return null;
    }
    cumulative_seconds += duration;
  }
  return cumulative_seconds;
}

/**
 * WorkoutStep object.
 */
export const WorkoutStep = function WorkoutStep(initial_values) {

  this.section_has_changes = false;
  this.equipment_ids = [-1];
  this.target_value = null;
  this.custom_target_value_low = null;
  this.custom_target_value_high = null;
  this.duration_value = null;
  this.proceed_off_seconds = null;
  this.step_rest_interval = null;

  this.set_values = function (values) {

    Object.keys(values).forEach(key => {
      let value = values[key];

      if (value && typeof(value) == 'object' && value != null && (value.constructor != Array)) {
        this[key] = objectAssignDeep({}, value);
      }
      else {
        this[key] = value;
      }
    });

    // Normalise this value.
    if (this.equipment_ids == null) {
      this.equipment_ids = [-1];
    }

    if ((
        this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE ||
        this.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE ||
        this.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE
      ) && !this.zone && this.custom_target_value_low) {
      this.zone = initial_values.custom_target_value_low;
    }
    else if (this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER && this.custom_target_value_low >= 1000) {
      this.custom_target_value_low = this.custom_target_value_low - 1000;
      this.custom_target_value_high = this.custom_target_value_high - 1000;
    }

  }

  if (initial_values) {
    this.set_values(initial_values);
  }

  if (!this.uuid) {
    this.uuid = uuidv1();
  }

}

/**
 * Flag that this step has been updated since being created.
 */
WorkoutStep.prototype.duplicate = function () {
  let duplicate_step = new WorkoutStep(this);
  duplicate_step.id = null;
  duplicate_step.repeat_step_index = null;
  duplicate_step.position_in_repeat = null;
  duplicate_step.uuid = uuidv1();
  return duplicate_step;
}

/**
 * Flag that this step has been updated since being created.
 */
WorkoutStep.prototype.markSectionHasChanges = function () {
  this.section_has_changes = true;
}

/**
 * Determine which of the main zones from 1 to 5 this step is in.
 */
WorkoutStep.prototype.get1to5Zone = function (training_zones, discipline) {

  if (this.zone <= 5) {
    return this.zone;
  }

  let zone_discipline_key = null;
  if (discipline == WORKOUT_DISCIPLINES.CYCLING) {
    zone_discipline_key = 'bike';
  }
  else if (discipline == WORKOUT_DISCIPLINES.RUNNING) {
    zone_discipline_key = 'run';
  }

  let metric = null;
  let zone_value = null;
  if (
    this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER ||
    this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP ||
    this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE
  ) {
    metric = 'watts';
    let ftp = training_zones && training_zones[zone_discipline_key] ? training_zones[zone_discipline_key].ftp : null;
    zone_value = this.getTargetWatts(ftp);
  }
  else if (this.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {
    metric = 'meters_per_second';
    zone_value = parseInt(Math.round(this.getTargetPaceSecondsPerMile()));
  }
  else if (this.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE) {
    metric = 'heart_rate';
    zone_value = this.getTargetHeartRate();
  }

  let step_zone = null;
  if (zone_discipline_key && training_zones && training_zones[zone_discipline_key].zones) {
    for (let i = 0; i < training_zones[zone_discipline_key].zones.length; i++) {
      let zone = training_zones[zone_discipline_key].zones[i];

      if (metric == 'watts') {
        if (!isNaN(zone.power_min) && !isNaN(zone.power_max) && zone.power_min && zone.power_max && zone_value >= zone.power_min && zone_value <= zone.power_max) {
          step_zone = zone.number;
        }
      }
      else if (metric == 'meters_per_second') {
        const zone_slowest_seconds_per_mile = !isNaN(zone.meters_per_second_slowest) ? parseInt(Math.round(metersPerSecondAsSecondsPerMile(zone.meters_per_second_slowest))) : null;
        const zone_fastest_seconds_per_mile = !isNaN(zone.meters_per_second_fastest) ? parseInt(Math.round(metersPerSecondAsSecondsPerMile(zone.meters_per_second_fastest))) : null;
        if (zone_slowest_seconds_per_mile && zone_slowest_seconds_per_mile && zone_value <= zone_slowest_seconds_per_mile && zone_value >= zone_fastest_seconds_per_mile) {
          step_zone = zone.number;
        }
      }
      else if (metric == 'heart_rate') {
        if (!isNaN(zone.heart_rate_min) && !isNaN(zone.heart_rate_max) && zone.heart_rate_min && zone.heart_rate_max && zone_value >= zone.heart_rate_min && zone_value <= zone.heart_rate_max) {
          step_zone = zone.number;
        }
      }

    }
  }

  return step_zone;

}

WorkoutStep.prototype.getZoneData = function (training_zones, discipline) {

  if (discipline == WORKOUT_DISCIPLINES.SWIMMING) {
    // Currently no zone data for swimming.
    return null;
  }
  else if (discipline == WORKOUT_DISCIPLINES.CYCLING) {
    discipline = 'bike';
  }
  else if (discipline == WORKOUT_DISCIPLINES.RUNNING) {
    discipline = 'run';
  }

  if (this.zone <= 5) {
    if (training_zones && training_zones[discipline].zones) {
      for (let i = 0; i < training_zones[discipline].zones.length; i++) {
        let zone = training_zones[discipline].zones[i];
        if (zone.number == this.zone) {
          if (
            (this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE && zone.power_min && zone.power_max) ||
            (this.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE && zone.meters_per_second_slowest && zone.meters_per_second_fastest) ||
            (this.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE && zone.heart_rate_min && zone.heart_rate_max)
          ) {
            return zone;
          }
        }
      }
    }
  }
  else {
    if (training_zones && training_zones[discipline].custom_zones) {
      return training_zones[discipline].custom_zones[this.zone];
    }
  }

  return null;
}

/**
 * Convert from FTP percentage if necessary given the FTP value.
 */
WorkoutStep.prototype.getTargetWatts = function (athlete_ftp) {
  if (this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
    let percentage_of_ftp = this.custom_target_value_high;
    return athlete_ftp * (percentage_of_ftp / 100);
  }
  else {
    return this.custom_target_value_high;
  }
}

WorkoutStep.prototype.getTargetHeartRate = function (training_zones, discipline) {
  let heart_rate = null;
  if (this.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE) {
    let zoneData = this.getZoneData(training_zones, discipline)
    heart_rate = (zoneData) ? (parseInt(zoneData.heart_rate_min) + parseInt(zoneData.heart_rate_max)) / 2 : 0;
  }
  else {
    heart_rate = (this.custom_target_value_low && this.custom_target_value_high) ? (parseInt(this.custom_target_value_low) + parseInt(this.custom_target_value_high)) / 2 : 0;
  }
  return heart_rate;
}

WorkoutStep.prototype.getTargetPaceMetersPerSecond = function (training_zones, discipline, min_or_max) {
  let meters_per_second = null;
  if (this.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE) {
    let zoneData = this.getZoneData(training_zones, discipline)
    if (min_or_max == 'min') {
      meters_per_second = (zoneData) ? zoneData.meters_per_second_slowest : 0;
    }
    else if (min_or_max == 'max') {
      meters_per_second = (zoneData) ? zoneData.meters_per_second_fastest: 0;
    }
    else {
      meters_per_second = (zoneData) ? (parseFloat(zoneData.meters_per_second_slowest) + parseFloat(zoneData.meters_per_second_fastest)) / 2 : 0;
    }
  }
  else {
    meters_per_second = this.custom_target_value_high / 1000;
  }
  return meters_per_second;
}

WorkoutStep.prototype.getTargetPaceSecondsPerMile = function (training_zones, discipline, min_or_max) {
  let meters_per_second = this.getTargetPaceMetersPerSecond(training_zones, discipline, min_or_max);
  return metersPerSecondAsSecondsPerMile(meters_per_second);
}

WorkoutStep.prototype.getTargetPaceSecondsPerKilometer = function (training_zones, discipline, min_or_max) {
  let meters_per_second = this.getTargetPaceMetersPerSecond(training_zones, discipline, min_or_max);
  return metersPerSecondAsSecondsPerKilometer(meters_per_second);
}

/**
 * Convert from watts to FTP percentage if necessary given the FTP value.
 */
WorkoutStep.prototype.getTargetPercentageFTP = function (athlete_ftp) {
  if (this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP) {
    return this.custom_target_value_high;
  }
  else {
    let watts = this.custom_target_value_high;
    return (watts / athlete_ftp) * 100;
  }
}

WorkoutStep.prototype.convertToWatts = function (athlete_ftp) {
  if (NORMAL_TARGET_TYPES.indexOf(this.target_type) != -1) {
    this.target_type = STEP_CONSTANTS.TARGET_TYPE_POWER;
  }
}

WorkoutStep.prototype.convertToFTPPercent = function (athlete_ftp) {
  if (NORMAL_TARGET_TYPES.indexOf(this.target_type) != -1) {
    this.target_type = STEP_CONSTANTS.TARGET_TYPE_POWER_PERCENTAGE_FTP;
  }
}

WorkoutStep.prototype.convertToPowerZone = function () {
  if (NORMAL_TARGET_TYPES.indexOf(this.target_type) != -1) {
    this.target_type = STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE;
  }
}

WorkoutStep.prototype.getDurationInSeconds = function () {
  if (this.duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME) {
    return this.duration_value;
  }
  else if (this.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE && this.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {
    let meters_per_second = this.getTargetPaceMetersPerSecond();
    let distance_in_meters = this.duration_value / 100;
    let seconds = distance_in_meters / meters_per_second;
    return seconds
  }
  else if (this.getProceedOffSeconds())
    return this.getProceedOffSeconds();
  else {
    return 0;
  }
}

WorkoutStep.prototype.getDurationInKM = function () {
  if (this.duration_type == STEP_CONSTANTS.DURATION_TYPE_DISTANCE) {
    return this.duration_value / 100000;
  }
  else if (this.duration_type == STEP_CONSTANTS.DURATION_TYPE_TIME && this.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED) {
    let meters_per_second = this.getTargetPaceMetersPerSecond();
    let time_in_seconds = this.duration_value;
    let distance_in_meters = meters_per_second * time_in_seconds;
    let km = distance_in_meters / 1000;
    return km;
  }
  else {
    return 0;
  }
}

/**
 * input_metric: 'seconds_per_mile' only at the moment
 */
WorkoutStep.prototype.setPace = function (value, input_metric) {
  let meters_per_second;
  if (input_metric == 'seconds_per_mile') {
    meters_per_second = secondsPerMileAsMetersPerSecond(value) * 1000;
  }
  else if (input_metric == 'seconds_per_kilometer') {
    meters_per_second = secondsPerKilometerAsMetersPerSecond(value) * 1000;
  }
  else {
    throw 'Invalid input_metric.'
  }
  this.custom_target_value_low = meters_per_second;
  this.custom_target_value_high = meters_per_second;
}

WorkoutStep.prototype.getSwimStrokeAsId = function () {
  return this.target_value;
}

WorkoutStep.prototype.getSwimStrokeAsText = function () {
  return SWIM_STROKES[this.target_value];
}

WorkoutStep.prototype.setSwimStroke = function (swim_stroke_id) {
  this.target_type = STEP_CONSTANTS.TARGET_TYPE_SWIM_STROKE;
  this.target_value = swim_stroke_id;
}

WorkoutStep.prototype.getSwimEquipmentAsText = function () {
  if (!this.equipment_ids || !this.equipment_ids.length) {
    return SWIM_EQUIPMENT[0];  // return 'no equipment' if equipment_ids is empty
  }

  const equipmentNames = this.equipment_ids.map(id => SWIM_EQUIPMENT[id]);

  if (equipmentNames.length === 1) {
    return equipmentNames[0];  // return the single item if there's only one
  }

  const lastEquipmentName = equipmentNames.pop();
  return `${equipmentNames.join(', ')} & ${lastEquipmentName}`;
}

WorkoutStep.prototype.toggleSwimEquipment = function (equipment_id) {

  equipment_id = parseInt(equipment_id);
  if (equipment_id === 0) {
    // If 'no equipment' is selected, ensure no other equipment is selected
    this.equipment_ids = [0];
  } else {
    // Remove 'no equipment' from the list if it's present
    this.equipment_ids = removeFromArrayIfExists(this.equipment_ids, 0);

    // Toggle the specified equipment_id
    this.equipment_ids = toggleInArray(this.equipment_ids, equipment_id);

    // If no equipment is selected, ensure 'no equipment' is added back
    if (this.equipment_ids.length === 0) {
      this.equipment_ids = [0];
    }
  }

};

WorkoutStep.prototype.setSwimEquipment = function (equipment_id) {
  this.equipment_ids = [equipment_id];
}

WorkoutStep.prototype.setIndividualStepRepetitions = function (repetitions) {
  repetitions = parseInt(repetitions)
  if (repetitions > 0) {
    this.individual_step_repetitions = repetitions;
  }
  else {
    this.individual_step_repetitions = null;
  }
}

WorkoutStep.prototype.getIndividualStepRepetitions = function (always_at_least_one) {
  if (this.individual_step_repetitions == null) {
    return (always_at_least_one) ? 1 : null;
  }
  else if (isNaN(parseInt(this.individual_step_repetitions))) {
    return 1;
  }
  else {
    return this.individual_step_repetitions;
  }
}

WorkoutStep.prototype.setProceedOffSeconds = function (seconds) {
  this.proceed_off_seconds = seconds;
}

WorkoutStep.prototype.getProceedOffSeconds = function () {
  return (isNaN(parseInt(this.proceed_off_seconds))) ? 0 : this.proceed_off_seconds;
}

WorkoutStep.prototype.setStepRestInterval = function (seconds) {
  this.step_rest_interval = seconds;
}

WorkoutStep.prototype.getStepRestInterval = function () {
  return (isNaN(parseInt(this.step_rest_interval))) ? 0 : this.step_rest_interval;
}

WorkoutStep.prototype.isRepeatStep = function () {
  return this.duration_type == STEP_CONSTANTS.DURATION_TYPE_REPEAT_QUANTITY;
}

WorkoutStep.prototype.getSaveData = function () {

  let workout_step = {};
  workout_step.id = this.id;
  workout_step.index = this.index;
  workout_step.duration_type = this.duration_type;
  workout_step.duration_value = this.duration_value;
  workout_step.target_type = this.target_type;
  workout_step.target_value = this.target_value;
  workout_step.note = this.note;
  workout_step.name = this.name;
  workout_step.equipment_ids = (this.equipment_ids[0] == -1) ? null : this.equipment_ids;
  workout_step.intensity = this.intensity;
  workout_step.proceed_off_seconds = this.proceed_off_seconds;
  workout_step.step_rest_interval = this.step_rest_interval;
  workout_step.individual_step_repetitions = this.individual_step_repetitions;

  if (
    this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER_BY_ZONE ||
    this.target_type == STEP_CONSTANTS.TARGET_TYPE_SPEED_BY_ZONE ||
    this.target_type == STEP_CONSTANTS.TARGET_TYPE_HEART_RATE_BY_ZONE
  ) {
    workout_step.custom_target_value_low = parseInt(this.zone);
    workout_step.custom_target_value_high = parseInt(this.zone);
  }
  else if (this.target_type == STEP_CONSTANTS.TARGET_TYPE_POWER) {
    if (this.custom_target_value_low >= 0) {
      workout_step.custom_target_value_low = this.custom_target_value_low + 1000;
    }
    if (this.custom_target_value_high >= 0) {
      workout_step.custom_target_value_high = this.custom_target_value_high + 1000;
    }
  }
  else {
    workout_step.custom_target_value_low = this.custom_target_value_low;
    workout_step.custom_target_value_high = this.custom_target_value_high;
  }

  return workout_step;
}
